import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { TextField, Button, Box, Alert, Link, CircularProgress, Typography } from '@mui/material';
import apiClient, { getErrorMessage } from '../../utils/axios';
import AuthCard, { authFieldSx, authLinkSx } from '../../components/AuthCard';
import { sanitizeReturnUrl, goToReturnUrl } from '../../utils/returnUrl';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const returnUrl = sanitizeReturnUrl(searchParams.get('returnUrl'));
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('재설정 링크가 올바르지 않습니다.');
      setChecking(false);
      return;
    }
    apiClient
      .get('/auth/reset-password/verify', { params: { token } })
      .then((res) => setAccountEmail(res.data?.email || ''))
      .catch((err) => setTokenError(err.response?.data?.error || '유효하지 않거나 만료된 링크입니다.'))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password) return setPasswordError('새 비밀번호를 입력하세요.');
    if (password.length < 6) return setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.');
    if (password !== confirmPassword) return setPasswordError('비밀번호가 일치하지 않습니다.');

    setLoading(true);
    try {
      const res = await apiClient.post('/auth/reset-password', { token, newPassword: password });
      if (returnUrl) {
        goToReturnUrl(returnUrl, res.data?.email || accountEmail, { reset: '1' });
        return;
      }
      navigate('/login', {
        replace: true,
        state: { notice: res.data?.message, email: res.data?.email || accountEmail },
      });
    } catch (err) {
      setError(getErrorMessage(err, '비밀번호 재설정 중 오류가 발생했습니다.'));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <AuthCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </AuthCard>
    );
  }

  if (tokenError) {
    return (
      <AuthCard title="링크가 만료되었습니다">
        <Alert severity="error" sx={{ mb: 3 }}>
          {tokenError}
        </Alert>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          재설정 링크는 발급 후 30분 동안 한 번만 사용할 수 있습니다. 새 링크를 다시 요청해주세요.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          component={RouterLink}
          to={returnUrl ? `/forgot-password?returnUrl=${encodeURIComponent(returnUrl)}` : '/forgot-password'}
          sx={{ py: 1.5, borderRadius: '4px' }}
        >
          재설정 메일 다시 받기
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          {returnUrl ? (
            <Link component="button" type="button" onClick={() => goToReturnUrl(returnUrl)} sx={authLinkSx}>
              로그인으로 돌아가기
            </Link>
          ) : (
            <Link component={RouterLink} to="/login" sx={authLinkSx}>
              로그인으로 돌아가기
            </Link>
          )}
        </Box>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="새 비밀번호 설정"
      subtitle={
        accountEmail ? (
          <>
            <strong>{accountEmail}</strong> 계정의 새 비밀번호를 입력해주세요. 변경하면 로그인된 모든 기기에서
            로그아웃됩니다.
          </>
        ) : (
          '새 비밀번호를 입력해주세요.'
        )
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          name="password"
          label="새 비밀번호"
          type="password"
          id="password"
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError(confirmPassword && e.target.value !== confirmPassword ? '비밀번호가 일치하지 않습니다.' : '');
          }}
          error={!!passwordError}
          helperText={passwordError || '6자 이상'}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          name="confirmPassword"
          label="새 비밀번호 재입력"
          type="password"
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setPasswordError(e.target.value !== password ? '비밀번호가 일치하지 않습니다.' : '');
          }}
          error={!!passwordError}
          sx={authFieldSx}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading || !!passwordError || !password || !confirmPassword}
          sx={{ mt: 1, py: 1.5, borderRadius: '4px' }}
        >
          {loading ? '변경 중...' : '비밀번호 변경'}
        </Button>
      </Box>
    </AuthCard>
  );
};

export default ResetPassword;
